import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  useTheme,
} from '@mui/material';
import { Invoice } from '../../../types';
import { formatCurrency, formatDate, daysRemaining } from '../../../utils/helpers';
import LaunchIcon from '@mui/icons-material/Launch';

interface InvoiceCardProps {
  invoice: Invoice;
  onViewInXero: (invoiceID: string) => void;
}

const InvoiceCard: React.FC<InvoiceCardProps> = ({ invoice, onViewInXero }) => {
  const theme = useTheme();
  const {
    invoiceID,
    invoiceNumber,
    name,
    date,
    dueDate,
    status,
    total,
    amountDue,
    amountPaid,
  } = invoice;

  // Calculate days remaining until due date
  const daysToPayment = daysRemaining(dueDate);
  
  // Determine status color and label based on new requirements
  const getStatusInfo = () => {
    const upperStatus = status.toUpperCase();
    
    // PAID = green
    if (upperStatus === 'PAID') {
      return {
        color: '#4caf50', // Green
        label: 'PAID'
      };
    }
    
    // DELETED/VOIDED = blue
    if (upperStatus === 'DELETED' || upperStatus === 'VOIDED') {
      return {
        color: '#2196f3', // Blue
        label: upperStatus
      };
    }
    
    // For all other statuses (not deleted, paid, or voided)
    // Check if overdue
    if (daysToPayment < 0) {
      return {
        color: '#f44336', // Red
        label: 'OVERDUE'
      };
    } else {
      return {
        color: '#ff9800', // Yellow/Orange
        label: 'AWAITING PAYMENT'
      };
    }
  };

  const statusInfo = getStatusInfo();

  // Format detailed status text for additional context
  const getDetailedStatusText = () => {
    const upperStatus = status.toUpperCase();
    
    if (upperStatus === 'PAID' || upperStatus === 'DELETED' || upperStatus === 'VOIDED') {
      return statusInfo.label;
    }
    
    // For awaiting payment or overdue
    if (daysToPayment < 0) {
      return `OVERDUE by ${Math.abs(daysToPayment)} days`;
    } else if (daysToPayment === 0) {
      return 'Due today';
    } else {
      return `Due in ${daysToPayment} days`;
    }
  };

  return (
    <Card
      sx={{
        mb: 1.5,
        borderLeft: `4px solid ${statusInfo.color}`,
        '&:hover': {
          boxShadow: theme.shadows[4],
        },
      }}
    >
      <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
        {/* Top Row: Name/Number and Status */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="h3" sx={{ fontSize: '1.1rem', fontWeight: 600, mb: 0.5 }}>
              {name || invoiceNumber}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="body2" color="text.secondary">
                #{invoiceNumber}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatDate(date)}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={statusInfo.label}
            size="small"
            sx={{
              backgroundColor: `${statusInfo.color}20`,
              color: statusInfo.color,
              fontWeight: 'bold',
              fontSize: '0.75rem',
            }}
          />
        </Box>

        {/* Bottom Row: Financial Info and Actions */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                Amount Due
              </Typography>
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                {formatCurrency(amountDue)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                Total
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                {formatCurrency(total)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                Due Date
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: statusInfo.color,
                  fontWeight: 'medium',
                  fontSize: '0.9rem'
                }}
              >
                {formatDate(dueDate)}
              </Typography>
            </Box>
            <Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: statusInfo.color,
                  fontWeight: 'medium',
                  fontSize: '0.85rem'
                }}
              >
                {getDetailedStatusText()}
              </Typography>
            </Box>
          </Box>
          
          <Button
            variant="outlined"
            size="small"
            endIcon={<LaunchIcon sx={{ fontSize: '0.9rem' }} />}
            onClick={() => onViewInXero(invoiceID)}
            sx={{ 
              minWidth: 'auto',
              px: 2,
              py: 0.5,
              fontSize: '0.8rem'
            }}
          >
            View in Xero
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

export default InvoiceCard;
